import _ from 'lodash';
import type { Action } from 'redux';
import { batchActions } from 'redux-batched-actions';

import * as CUSTOM_DASHBOARD from 'constants/custom_dashboard';
import AppData from 'app_data';
import type { CustomDashboardState, DashboardTile } from 'reducers/custom_dashboard_reducer';

type Dispatch = (action: unknown) => unknown;
type GetState = () => { custom_dashboard: CustomDashboardState };

function loadDashboardRequest() {
  return { type: CUSTOM_DASHBOARD.LOAD_DASHBOARD_REQUEST };
}

function loadDashboardSuccess(response: { data: unknown }) {
  const { data } = response;
  return { type: CUSTOM_DASHBOARD.LOAD_DASHBOARD_SUCCESS, data };
}

// Exported, unlike its request/success siblings: the dashboard's own mount
// handler has to report a failed PREREQUISITE (the custom-reports list) the
// same way, or isLoading -- which starts true -- never clears and the view
// spins forever.
export function loadDashboardFailure(error: unknown) {
  return { type: CUSTOM_DASHBOARD.LOAD_DASHBOARD_FAILURE, error };
}

export function loadDashboard(id: string | number, viewing?: boolean) {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(loadDashboardRequest());
    return AppData.getDashboard(id)
      .then((response: { data: unknown }) => {
        if (!viewing) {
          dispatch(batchActions([
            loadDashboardSuccess(response),
            toggleEditDashboard(),
          ]));
          return response;
        }
        dispatch(loadDashboardSuccess(response));
      })
      .catch((error: unknown) => {
        dispatch(loadDashboardFailure(error));
      });
  };
}

function saveDashboardRequest() {
  return { type: CUSTOM_DASHBOARD.SAVE_DASHBOARD_REQUEST };
}

function saveDashboardSuccess(response: { data: unknown }) {
  const { data } = response;
  return { type: CUSTOM_DASHBOARD.SAVE_DASHBOARD_SUCCESS, data };
}

function saveDashboardFailure(error: unknown) {
  return { type: CUSTOM_DASHBOARD.SAVE_DASHBOARD_FAILURE, error };
}

export function saveDashboard(dashboardId?: string | number) {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(saveDashboardRequest);
    const dashboardState = getState().custom_dashboard;
    const savedDashboard = getDashboardInfo(dashboardState);
    if (dashboardId) {
      savedDashboard.dashboardId = dashboardId;
    }

    return AppData.saveDashboard(savedDashboard)
      .then((response: { data: unknown }) => {
        dispatch(saveDashboardSuccess(response));
      }, (error: unknown) => {
        dispatch(saveDashboardFailure(error));
      });
  };
}

export function startNewDashboard() {
  // Returns the chain, like loadDashboard() does, so callers and tests can
  // await it.
  return (dispatch: Dispatch, getState: GetState) => {
    return AppData.getNextosGlobalSettings()
      .then((response: { data: { time_settings: { monitoring: unknown } } }) => {
        const { data } = response;
        dispatch(batchActions([
          // as Action: batchActions' type rejects the extra keys on a fresh literal.
          { type: CUSTOM_DASHBOARD.SET_TIME_RANGE, field: 'timeRange', value: data.time_settings.monitoring } as Action,
          { type: CUSTOM_DASHBOARD.START_NEW_DASHBOARD },
        ]));
      })
      // START_NEW_DASHBOARD is what clears isLoading, and it only dispatches
      // from the success path above -- so without this the no-id route
      // (/monitoring/custom with no dashboardId) spins forever whenever the
      // settings call fails.
      .catch((error: unknown) => {
        dispatch(loadDashboardFailure(error));
      });
  };
}

export function toggleEditDashboard() {
  return { type: CUSTOM_DASHBOARD.TOGGLE_EDIT_DASHBOARD };
}

export function addTile() {
  return { type: CUSTOM_DASHBOARD.ADD_TILE };
}

export function setTile(tileType: string, elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.SET_TILE, tileType, index: elementIndex };
}

export function updateTile(tileState: Record<string, unknown>, elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.UPDATE_TILE, tileState, index: elementIndex };
}

export function removeTile(elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.REMOVE_TILE, index: elementIndex };
}

export function editTile(elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.EDIT_TILE, index: elementIndex };
}

export function cancel(elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.CANCEL, index: elementIndex };
}

export function cancelNew(elementIndex: number) {
  return { type: CUSTOM_DASHBOARD.CANCEL_NEW, index: elementIndex, tileType: 'new' };
}

export function layoutChange(layout: unknown[]) {
  return { type: CUSTOM_DASHBOARD.LAYOUT_CHANGE, layout };
}

export function breakpointChange(breakpoint: string, cols: number) {
  return { type: CUSTOM_DASHBOARD.BREAKPOINT_CHANGE, breakpoint, cols };
}

export function validate(options: { excludeTitle?: boolean }) {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: CUSTOM_DASHBOARD.VALIDATE, options });

    const errors = getState().custom_dashboard.errors;
    const messages: string[] = [];
    _.each(errors, (err) => {
      if (err.hasError) messages.push(err.errorMessage);
    });

    return messages.length > 0 ? messages : null;
  };
}

export function setStatus(value: string) {
  return { type: CUSTOM_DASHBOARD.SET_STATUS, field: 'status', value };
}

export function setTitle(value: string) {
  return { type: CUSTOM_DASHBOARD.SET_TITLE, field: 'title', value };
}

export function setDescription(value: string) {
  return { type: CUSTOM_DASHBOARD.SET_DESCRIPTION, field: 'description', value };
}

export function setTimeRange(value: unknown) {
  return { type: CUSTOM_DASHBOARD.SET_TIME_RANGE, field: 'timeRange', value };
}

export function setRealTime(value: boolean) {
  return { type: CUSTOM_DASHBOARD.SET_REAL_TIME, field: 'realTime', value };
}

export function setOptionsPage(value: number) {
  return { type: CUSTOM_DASHBOARD.SET_OPTIONS_PAGE, field: 'tile_options_page', value };
}

export function clear() {
  return { type: CUSTOM_DASHBOARD.CLEAR };
}

function getDashboardInfo(dashboardState: CustomDashboardState) {
  // The payload POSTed to /nextos/dashboard; dashboardId is added only on update.
  const dashboard = {} as { title: string | null; time_range: unknown; description: string | null; items: DashboardTile[]; dashboardId?: string | number };
  const uiState = dashboardState;

  dashboard.title = uiState.title;
  dashboard.time_range = uiState.timeRange;
  dashboard.description = uiState.description;
  dashboard.items = uiState.items;
  dashboard.items = dashboard.items.map((item, index) => {
    item.layout = uiState.layout[index];
    return item;
  });

  return dashboard;
}
